import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notification } from "antd";
import { useTranslation } from "react-i18next";

import api from "../../../api";
import { getServerErrorNotification } from "../../../utils/error";
import { mapZmUsers, toZmRequestParams } from "./mapZmUsers";
import { IZmUser, ZmFilterOptions } from "./types";

export function useZmUsers(
  filterOptions: ZmFilterOptions,
  { initialPage = 1 }: { initialPage?: number } = {}
) {
  const { t } = useTranslation("ZmView");
  const [page, setPage] = useState(initialPage);
  const [users, setUsers] = useState<IZmUser[]>([]);
  const [loading, setLoading] = useState(false);
  // Only the very first load should resume at `initialPage` (SMT-1354: a
  // page round-tripped through the URL). Every later filter change still
  // resets to page 1, same as before.
  const isFirstLoad = useRef(true);

  const loadUsers = useCallback(
    async (filters: ZmFilterOptions, pageNumber: number) => {
      if (!filters.dateFrom || !filters.dateTo) {
        return;
      }

      setLoading(true);
      try {
        const result = await api.kontax.getUsersWithZmTransactions(
          toZmRequestParams(filters, pageNumber)
        );
        setPage(result.page);
        setUsers(mapZmUsers(result.users));
      } catch (error) {
        const { message: title, description } = getServerErrorNotification(
          error,
          t("notifications.fetchUsersError")
        );
        notification.error({
          message: title,
          ...(description ? { description } : {}),
        });
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      loadUsers(filterOptions, initialPage);
      return;
    }
    setPage(1);
    loadUsers(filterOptions, 1);
  }, [filterOptions, loadUsers, initialPage]);

  const hasUsers = users.length > 0;

  const pagination = useMemo(
    () => ({
      page,
      goToPrevious: () => {
        if (page <= 1) {
          return;
        }
        const nextPage = page - 1;
        setPage(nextPage);
        loadUsers(filterOptions, nextPage);
      },
      goToNext: () => {
        const nextPage = page + 1;
        setPage(nextPage);
        loadUsers(filterOptions, nextPage);
      },
    }),
    [filterOptions, loadUsers, page]
  );

  return {
    users,
    loading,
    page,
    hasUsers,
    pagination,
  };
}
